import React from 'react';
import ElectionsSection from './ElectionsSection';
import ContractSection from './ContractSection';
import TrainingQuizSection from './TrainingQuizSection';
import NewShipments from './NewShipments';
import AssignmentsSection from './AssignmentsSection';
import SurveySection from './SurveySection';
import PaymentsSection from './PaymentsSection';
import EDayCheckIn from './EDayCheckIn';

/*
  The dashboard's widget order, width and per-status visibility live in the
  Quickbase "Widget Layout" table and arrive from /api/getDashboardLayout. This
  module is the other half of that contract: it maps a layout entry's key onto the
  component that renders it, and carries the fallback layout used when the API
  can't be reached.

  Keys match the HTML Key field on Source: Widgets. They double as the DOM ids the
  widgets already render with, so the Quickbase records stay legible next to the
  markup - but nothing here reads the DOM, and the ids stay owned by the
  components themselves.
*/

// A widget only offers Half where the content actually survives a narrow column.
// Quickbase can't know that, so an unsupported width is clamped rather than
// trusted - see widthFor().
export const WIDGETS = {
  'elections-section': {
    allowedWidths: ['Full'],
    render: (ctx) => (
      <ElectionsSection
        elections={ctx.elections}
        dj={ctx.dj}
        electionContext={ctx.electionContext}
        handleApplyToElection={ctx.handleApplyToElection}
        handleOpenVoterRegistration={ctx.handleOpenVoterRegistration}
        nextSteps={ctx.nextSteps}
      />
    ),
  },
  'contract-section': {
    allowedWidths: ['Full', 'Half'],
    render: (ctx) => (
      <ContractSection dj={ctx.dj} elections={ctx.elections} setElections={ctx.setElections} />
    ),
  },
  'training-quiz-section': {
    allowedWidths: ['Full', 'Half'],
    render: (ctx) => (
      <TrainingQuizSection djElectionRid={ctx.djElection?.rid} />
    ),
  },
  'deliveries-section': {
    allowedWidths: ['Full', 'Half'],
    render: (ctx) => (
      <NewShipments djElection={ctx.djElection || null} />
    ),
  },
  'assignments-section': {
    allowedWidths: ['Full', 'Half'],
    render: (ctx) => (
      <AssignmentsSection
        elections={ctx.elections}
        handleConfirmShift={ctx.handleConfirmShift}
        handleRejectShift={ctx.handleRejectShift}
        assignmentConfirmLoading={ctx.assignmentConfirmLoading}
        assignmentConfirmMessage={ctx.assignmentConfirmMessage}
        assignmentRejectLoading={ctx.assignmentRejectLoading}
        assignmentRejectMessage={ctx.assignmentRejectMessage}
      />
    ),
  },
  'survey-section': {
    allowedWidths: ['Full', 'Half'],
    render: (ctx) => (
      <SurveySection
        dj={ctx.dj}
        djElection={ctx.djElection}
      />
    ),
  },
  'payments-section': {
    allowedWidths: ['Full', 'Half'],
    render: (ctx) => (
      <PaymentsSection djElection={ctx.djElection} />
    ),
  },
  'eday-checkin-section': {
    allowedWidths: ['Full'],
    render: (ctx) => (
      <EDayCheckIn elections={ctx.elections} setElections={ctx.setElections} dj={ctx.dj} setDj={ctx.setDj} />
    ),
  },
};

// Two per row at most, collapsing to one below lg. lg matches the breakpoint the
// Shipments/Assignments pair used before this was configurable.
const SPAN = {
  Full: 'col-span-12',
  Half: 'col-span-12 lg:col-span-6',
};

export function widthFor(key, width) {
  const entry = WIDGETS[key];
  if (entry && entry.allowedWidths.includes(width)) return width;
  return 'Full';
}

export function spanClass(width) {
  return SPAN[width] || SPAN.Full;
}

/*
  Fallback only. This is the layout as it was hardcoded before Quickbase drove it,
  and it is used when /api/getDashboardLayout errors - never when it returns an
  empty layout, which is valid data for a DJ early in the funnel.

  `statuses: null` means the widget has no status gate at all.
*/
const DEFAULT_WIDGETS = [
  { key: 'elections-section', order: 1, width: 'Full', statuses: null },
  { key: 'contract-section', order: 2, width: 'Full', statuses: ['Hired', 'Contract Signed'] },
  { key: 'training-quiz-section', order: 3, width: 'Full', statuses: ['Contract Signed', 'Assignment Matched', 'Assignment Confirmed', 'Paid'] },
  { key: 'deliveries-section', order: 4, width: 'Half', statuses: ['Hired', 'Contract Signed', 'Assignment Matched', 'Assignment Confirmed'] },
  { key: 'assignments-section', order: 5, width: 'Half', statuses: ['Contract Signed', 'Assignment Matched', 'Assignment Confirmed'] },
  { key: 'survey-section', order: 6, width: 'Full', statuses: ['Contract Signed', 'Assignment Matched', 'Assignment Confirmed'] },
  { key: 'payments-section', order: 7, width: 'Full', statuses: ['Hired', 'Contract Signed', 'Assignment Matched', 'Assignment Confirmed'] },
  { key: 'eday-checkin-section', order: 8, width: 'Full', statuses: ['Assignment Matched', 'Assignment Confirmed'] },
];

export function resolveFallbackLayout(status) {
  return DEFAULT_WIDGETS
    .filter((w) => w.statuses === null || w.statuses.includes(status))
    .map(({ key, order, width }) => ({ key, order, width }));
}

/*
  Payments is the one rule Quickbase can't express: a DJ holding payment records
  sees it whatever their status. That was the `|| payments?.length > 0` clause on
  the old showPayments gate, and it has to survive as code.
*/
export function applyPaymentsOverride(layout, djElection) {
  const key = 'payments-section';
  if (!(djElection?.payments?.length > 0)) return layout;
  if (layout.some((w) => w.key === key)) return layout;

  const fallback = DEFAULT_WIDGETS.find((w) => w.key === key);
  return [...layout, { key, order: fallback.order, width: fallback.width }]
    .sort((a, b) => a.order - b.order || a.key.localeCompare(b.key));
}
